import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setUser } from '../../store/slices/userSlice';
import authService, { AuthResponse, ValidateUserResponse } from '../../services/authService';
import {
  getLoginAuthSettings,
  formatUsernameInput,
  normalizeUsernameForSubmit,
  validateUsername,
  LOGIN_FORM_AUTOCOMPLETE,
} from '../../config/authConfig';
import {
  LOGIN_GO_KEYBOARD_PROPS,
  useLoginFieldFocus,
  handleEnterOrGoKeyDown,
} from '../../utils/loginKeyboard';
import AuthMessage from '../AuthMessage/AuthMessage';
import { useKioskMode } from '../../contexts/KioskModeContext';
import { kioskMenuPath, kioskOrderTypePath } from './kioskPaths';
import styles from './KioskLogin.module.css';

const KioskLogin: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, authReady } = useAppSelector((s) => s.user);
  const { orderTypes, resetKioskGuestSessionUi } = useKioskMode();

  useEffect(() => {
    resetKioskGuestSessionUi();
  }, [resetKioskGuestSessionUi]);

  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const formRef = React.useRef<HTMLFormElement>(null);
  const usernameRef = React.useRef<HTMLInputElement>(null);
  const otpFirstRef = React.useRef<HTMLInputElement>(null);

  const loginAuth = getLoginAuthSettings(false);

  const goAfterLogin = React.useCallback(() => {
    if (!appName) return;
    if (orderTypes.length <= 1) {
      navigate(kioskMenuPath(appName), { replace: true });
    } else {
      navigate(kioskOrderTypePath(appName), { replace: true });
    }
  }, [appName, navigate, orderTypes.length]);

  useEffect(() => {
    if (authReady && isAuthenticated) {
      goAfterLogin();
    }
  }, [authReady, isAuthenticated, goAfterLogin]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useLoginFieldFocus(usernameRef, step === 1 && !loading);
  useLoginFieldFocus(otpFirstRef, step === 2 && !loading);

  const handleSendOtp = async () => {
    const validationError = validateUsername(username, loginAuth);
    if (validationError) {
      setError(validationError);
      return;
    }
    const normalized = normalizeUsernameForSubmit(username, loginAuth);
    if (normalized !== username) setUsername(normalized);
    setLoading(true);
    setError('');
    try {
      const response: ValidateUserResponse = await authService.validateUser(normalized);
      if (response.success) {
        setStep(2);
        setResendCooldown(response.otpCooldownSeconds || 30);
      } else {
        setError(response.error || 'Could not send OTP');
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'An error occurred';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 4) {
      setError('Enter the 4-digit OTP');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const normalized = normalizeUsernameForSubmit(username, loginAuth);
      const response: AuthResponse = await authService.login(normalized, otpString);
      if (response.success) {
        const userResponse = await authService.getCurrentUser();
        if (userResponse.success && userResponse.user) {
          dispatch(setUser(userResponse.user));
        }
        goAfterLogin();
      } else {
        setError(response.message || 'Invalid OTP');
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'An error occurred';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const next = [...otp];
    next[index] = value.replace(/\D/g, '');
    setOtp(next);
    if (value && index < 3) {
      document.getElementById(`kiosk-otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`kiosk-otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4).split('');
    if (!digits.length) return;
    const next = [...otp];
    digits.forEach((d, i) => {
      if (i < 4) next[i] = d;
    });
    setOtp(next);
    document.getElementById(`kiosk-otp-${Math.min(digits.length, 4) - 1}`)?.focus();
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>Start your order</h1>
        <p className={styles.subtitle}>Enter your mobile number to continue</p>

        {error && <AuthMessage variant="error">{error}</AuthMessage>}

        <form
          ref={formRef}
          className={styles.fieldBlock}
          onSubmit={(e) => {
            e.preventDefault();
            if (step === 1) void handleSendOtp();
            else void handleVerifyOtp();
          }}
        >
        {step === 1 ? (
          <>
            <label className={styles.label} htmlFor="kiosk-phone">
              Mobile number
            </label>
            <input
              id="kiosk-phone"
              ref={usernameRef}
              className={styles.input}
              type="tel"
              inputMode="numeric"
              autoComplete={LOGIN_FORM_AUTOCOMPLETE.username}
              value={username}
              onChange={(e) => setUsername(formatUsernameInput(e.target.value, loginAuth))}
              onKeyDown={(e) => handleEnterOrGoKeyDown(e, formRef)}
              {...LOGIN_GO_KEYBOARD_PROPS}
              disabled={loading}
            />
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={loading || !username.trim()}
            >
              {loading ? 'Sending…' : 'Send OTP'}
            </button>
          </>
        ) : (
          <>
            <p className={styles.otpHint}>OTP sent to {username}</p>
            <div className={styles.otpRow}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`kiosk-otp-${index}`}
                  ref={index === 0 ? otpFirstRef : undefined}
                  className={styles.otpInput}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  disabled={loading}
                  aria-label={`OTP digit ${index + 1}`}
                />
              ))}
            </div>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={loading || otp.join('').length !== 4}
            >
              {loading ? 'Verifying…' : 'Continue'}
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled={loading || resendCooldown > 0}
              onClick={() => void handleSendOtp()}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
            </button>
            <button
              type="button"
              className={styles.linkBtn}
              disabled={loading}
              onClick={() => {
                setStep(1);
                setOtp(['', '', '', '']);
                setError('');
              }}
            >
              Change number
            </button>
          </>
        )}
        </form>
      </div>
    </div>
  );
};

export default KioskLogin;
